/**
 * Axios GET request deduplication.
 *
 * If an identical GET request (same URL + params) is already in-flight,
 * the duplicate caller receives the same promise instead of firing a new one.
 *
 * - Only deduplicates GET requests (safe; no side effects)
 * - Cache entry lives while the request is in-flight + a 2s grace period
 *   to also prevent rapid sequential duplicates (e.g. mount + watcher both fire)
 */

const inflight = new Map();
const GRACE_MS = 2000;

function buildKey(baseURL, url, params) {
  // If url is absolute (starts with http), don't prepend baseURL
  const fullUrl = /^https?:\/\//.test(url) ? url : `${baseURL}${url}`;

  if (!params) return fullUrl;

  // Normalize: sort keys and coerce values to strings
  // so that { group_id: 40 } and { group_id: "40" } produce the same key
  const keys = Object.keys(params).sort();
  const normalized = keys.map((k) => `${k}=${params[k]}`).join("&");
  return `${fullUrl}?${normalized}`;
}

/**
 * Install GET deduplication on an axios instance.
 * Wraps .get() so identical concurrent calls share one network request.
 */
export function installDedup(axiosInstance) {
  const originalGet = axiosInstance.get.bind(axiosInstance);

  axiosInstance.get = function dedupGet(url, config = {}) {
    const key = buildKey(
      axiosInstance.defaults.baseURL || "",
      url,
      config.params
    );

    const cached = inflight.get(key);
    if (cached) {
      return cached;
    }

    const promise = originalGet(url, config)
      .then((res) => {
        // Schedule removal after grace period
        setTimeout(() => {
          if (inflight.get(key) === promise) {
            inflight.delete(key);
          }
        }, GRACE_MS);
        return res;
      })
      .catch((err) => {
        // On error, remove immediately so retries are not blocked
        inflight.delete(key);
        throw err;
      });

    inflight.set(key, promise);
    return promise;
  };
}
